import React, { useCallback, useState } from "react";
import { AlertTriangle, Copy, Check, Pencil, Plus, Tag, Trash2, X } from "lucide-react";

import Topbar from "../components/Topbar";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  TableSkeleton,
  useToast,
} from "../components/ui/Feedback";
import { Pagination } from "../components/ui/Table";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { friendlyError } from "../lib/errorMessage";

/**
 * Discount codes.
 *
 * New page. The `discount_codes` table exists, the cart redeems against it,
 * `discount_code_uses` records redemptions, and the RLS policies already grant
 * admins insert/update/delete — but nothing in this panel ever created a code.
 * The whole feature was reachable only by writing SQL by hand.
 *
 * Every editable column is here: name, code, type, value, currency,
 * min_order_amount, max_uses, one_per_user, is_active and expires_at.
 * `uses_count` is shown but not editable — it's a running total the checkout
 * maintains, and letting someone type over it would silently reopen a code
 * that had legitimately run out.
 */

const PAGE_SIZE = 30;

type FormValues = {
  name: string;
  code: string;
  type: "percentage" | "fixed";
  value: string;
  currency: string;
  min_order_amount: string;
  max_uses: string;
  one_per_user: boolean;
  is_active: boolean;
  expires_at: string;
};

const emptyForm: FormValues = {
  name: "",
  code: "",
  type: "percentage",
  value: "",
  currency: "",
  min_order_amount: "0",
  max_uses: "",
  one_per_user: true,
  is_active: true,
  expires_at: "",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  marginBottom: 6,
};

const hintStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--text-muted)",
  marginTop: 5,
  lineHeight: 1.45,
};

const date = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

/** A code is spent, expired or switched off — anything that makes it not work. */
function statusOf(row: any): { label: string; className: string } {
  if (!row.is_active) return { label: "Inactive", className: "badge" };
  if (row.expires_at && new Date(row.expires_at) < new Date()) {
    return { label: "Expired", className: "badge badge-danger" };
  }
  if (row.max_uses != null && (row.uses_count ?? 0) >= row.max_uses) {
    return { label: "Used up", className: "badge badge-warning" };
  }
  return { label: "Live", className: "badge badge-success" };
}

export default function Discounts() {
  const toast = useToast();
  const [offset, setOffset] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [values, setValues] = useState<FormValues>(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<any | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const list = useQuery(async () => {
    const { data, error, count } = await supabase
      .from("discount_codes")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    return { rows: data ?? [], total: count ?? 0 };
  }, [offset]);

  const rows = list.data?.rows ?? [];

  const openAdd = useCallback(() => {
    setEditing(null);
    setValues(emptyForm);
    setFormError("");
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: any) => {
    setEditing(row);
    setValues({
      name: row.name ?? "",
      code: row.code ?? "",
      type: row.type ?? "percentage",
      value: row.value != null ? String(row.value) : "",
      currency: row.currency ?? "",
      min_order_amount: row.min_order_amount != null ? String(row.min_order_amount) : "0",
      max_uses: row.max_uses != null ? String(row.max_uses) : "",
      one_per_user: row.one_per_user ?? true,
      is_active: row.is_active ?? true,
      // datetime-local wants `YYYY-MM-DDTHH:mm` with no zone or seconds.
      expires_at: row.expires_at ? new Date(row.expires_at).toISOString().slice(0, 16) : "",
    });
    setFormError("");
    setModalOpen(true);
  }, []);

  function validate(): string | null {
    if (!values.name.trim()) return "Give the code an internal name";
    if (!values.code.trim()) return "Set the code customers will type";
    const amount = Number(values.value);
    if (!(amount > 0)) return "Value must be above zero";
    if (values.type === "percentage" && amount > 100) {
      return "A percentage discount can't be above 100";
    }
    if (values.type === "fixed" && !values.currency.trim()) {
      return "A fixed-amount discount needs a currency — £10 off and $10 off are different discounts";
    }
    if (values.max_uses.trim() && !(Number(values.max_uses) > 0)) {
      return "Max uses must be a whole number above zero, or blank for unlimited";
    }
    return null;
  }

  async function save() {
    const problem = validate();
    if (problem) {
      setFormError(problem);
      return;
    }

    setFormError("");
    setSaving(true);

    const payload = {
      name: values.name.trim(),
      // Uppercased so redemption isn't case-sensitive by accident — customers
      // type these off a screenshot.
      code: values.code.trim().toUpperCase(),
      type: values.type,
      value: Number(values.value),
      currency: values.type === "fixed" ? values.currency.trim().toUpperCase() : null,
      min_order_amount: Number(values.min_order_amount) || 0,
      max_uses: values.max_uses.trim() ? Number(values.max_uses) : null,
      one_per_user: values.one_per_user,
      is_active: values.is_active,
      expires_at: values.expires_at ? new Date(values.expires_at).toISOString() : null,
    };

    const { error } = editing
      ? await supabase.from("discount_codes").update(payload).eq("id", editing.id)
      : await supabase.from("discount_codes").insert(payload);

    setSaving(false);

    if (error) {
      // The unique constraint on `code` surfaces here, which is the most
      // common failure and worth showing verbatim.
      setFormError(error.message);
      return;
    }

    toast.success(editing ? "Code updated" : "Code created");
    setModalOpen(false);
    list.refetch();
  }

  async function confirmDelete() {
    if (!deleting) return;
    setDeletingBusy(true);
    const { error } = await supabase.from("discount_codes").delete().eq("id", deleting.id);
    setDeletingBusy(false);
    if (error) {
      toast.failure(friendlyError(error, "Something went wrong. Please try again."));
      return;
    }
    toast.success("Code deleted");
    setDeleting(null);
    list.refetch();
  }

  function copyCode(code: string) {
    navigator.clipboard.writeText(code);
    setCopied(code);
    window.setTimeout(() => setCopied(null), 1500);
  }

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  return (
    <div>
      <Topbar
        title="Discount codes"
        subtitle={list.data ? `${list.data.total} code${list.data.total === 1 ? "" : "s"}` : undefined}
        actions={
          <button className="btn btn-primary" onClick={openAdd}>
            <Plus size={15} /> New code
          </button>
        }
      />

      <div className="page-shell">
        <div className="card">
          {list.error ? (
            <ErrorState error={list.error} onRetry={list.refetch} />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Discount</th>
                      <th>Minimum</th>
                      <th>Uses</th>
                      <th>Expires</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading && <TableSkeleton rows={6} cols={7} />}

                    {!list.loading &&
                      rows.map((row: any) => {
                        const status = statusOf(row);
                        return (
                          <tr key={row.id} onClick={() => openEdit(row)} style={{ cursor: "pointer" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <span
                                  style={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: 9,
                                    background: "var(--primary-light)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Tag size={13} color="var(--primary)" />
                                </span>
                                <div>
                                  <div
                                    style={{
                                      fontWeight: 800,
                                      fontFamily: "ui-monospace, Menlo, monospace",
                                      color: "var(--text-primary)",
                                      letterSpacing: "0.02em",
                                    }}
                                  >
                                    {row.code}
                                  </div>
                                  <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                    {row.name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ fontWeight: 750 }}>
                              {row.type === "percentage"
                                ? `${row.value}% off`
                                : `${row.currency ?? ""} ${row.value} off`}
                            </td>
                            <td>
                              {Number(row.min_order_amount) > 0
                                ? `${row.currency ?? ""} ${row.min_order_amount}`
                                : "—"}
                            </td>
                            <td>
                              <strong>{row.uses_count ?? 0}</strong>
                              <span style={{ color: "var(--text-muted)" }}>
                                {row.max_uses != null ? ` / ${row.max_uses}` : " / ∞"}
                              </span>
                              {row.one_per_user && (
                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                                  1 per customer
                                </div>
                              )}
                            </td>
                            <td>{date(row.expires_at)}</td>
                            <td>
                              <span className={status.className}>{status.label}</span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                <button
                                  className="btn btn-sm"
                                  title="Copy code"
                                  onClick={() => copyCode(row.code)}
                                  style={{
                                    background: "var(--primary-light)",
                                    color: "var(--primary)",
                                    border: "none",
                                  }}
                                >
                                  {copied === row.code ? <Check size={13} /> : <Copy size={13} />}
                                </button>
                                <button
                                  className="btn btn-sm"
                                  title="Edit"
                                  onClick={() => openEdit(row)}
                                  style={{
                                    background: "var(--primary-light)",
                                    color: "var(--primary)",
                                    border: "none",
                                  }}
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  title="Delete"
                                  onClick={() => setDeleting(row)}
                                  style={{ border: "none" }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                    {!list.loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <EmptyState
                            message="No discount codes yet"
                            hint="The cart already redeems these — create one and it works immediately."
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                offset={offset}
                limit={PAGE_SIZE}
                total={list.data?.total ?? 0}
                onChange={setOffset}
                busy={list.fetching}
              />
            </>
          )}
        </div>
      </div>

      {modalOpen && (
        <div
          onClick={() => setModalOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(24, 27, 46, 0.42)",
            backdropFilter: "blur(3px)",
            zIndex: 400,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-md)",
              width: "100%",
              maxWidth: 620,
            }}
          >
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "20px 24px",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 850, letterSpacing: "-0.03em" }}>
                {editing ? "Edit code" : "New discount code"}
              </div>
              <button className="btn btn-sm" onClick={() => setModalOpen(false)} aria-label="Close">
                <X size={14} />
              </button>
            </header>

            <div style={{ padding: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Internal name</label>
                  <input
                    className="input"
                    value={values.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Launch week 20%"
                  />
                  <div style={hintStyle}>Only you see this.</div>
                </div>
                <div>
                  <label style={labelStyle}>Code</label>
                  <input
                    className="input"
                    value={values.code}
                    onChange={(e) => set("code", e.target.value.toUpperCase())}
                    placeholder="LAUNCH20"
                    style={{ fontFamily: "ui-monospace, Menlo, monospace", letterSpacing: "0.04em" }}
                  />
                  <div style={hintStyle}>What the customer types. Saved uppercase.</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Type</label>
                  <select
                    className="input"
                    value={values.type}
                    onChange={(e) => set("type", e.target.value as FormValues["type"])}
                  >
                    <option value="percentage">Percentage off</option>
                    <option value="fixed">Fixed amount off</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Value</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step="0.01"
                    value={values.value}
                    onChange={(e) => set("value", e.target.value)}
                    placeholder={values.type === "percentage" ? "20" : "10.00"}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <select
                    className="input"
                    value={values.currency}
                    onChange={(e) => set("currency", e.target.value)}
                    disabled={values.type === "percentage"}
                    style={{ opacity: values.type === "percentage" ? 0.5 : 1 }}
                  >
                    <option value="">—</option>
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                  </select>
                  <div style={hintStyle}>
                    {values.type === "percentage"
                      ? "Percentages apply in any currency."
                      : "£10 off and $10 off are different discounts."}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Minimum order</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step="0.01"
                    value={values.min_order_amount}
                    onChange={(e) => set("min_order_amount", e.target.value)}
                  />
                  <div style={hintStyle}>0 for no minimum.</div>
                </div>
                <div>
                  <label style={labelStyle}>Max uses</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    step={1}
                    value={values.max_uses}
                    onChange={(e) => set("max_uses", e.target.value)}
                    placeholder="Unlimited"
                  />
                  <div style={hintStyle}>
                    {editing ? `Used ${editing.uses_count ?? 0} times so far.` : "Blank for unlimited."}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Expires</label>
                  <input
                    className="input"
                    type="datetime-local"
                    value={values.expires_at}
                    onChange={(e) => set("expires_at", e.target.value)}
                  />
                  <div style={hintStyle}>Blank never expires.</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <Toggle
                  on={values.is_active}
                  onClick={() => set("is_active", !values.is_active)}
                  onLabel="Active"
                  offLabel="Switched off"
                />
                <Toggle
                  on={values.one_per_user}
                  onClick={() => set("one_per_user", !values.one_per_user)}
                  onLabel="One per customer"
                  offLabel="Reusable per customer"
                />
              </div>

              {formError && (
                <div
                  style={{
                    display: "flex",
                    gap: 9,
                    alignItems: "flex-start",
                    background: "var(--danger-light)",
                    border: "1px solid #f6c9cf",
                    borderRadius: "var(--radius)",
                    padding: "11px 13px",
                    fontSize: 13,
                    color: "var(--danger)",
                    fontWeight: 650,
                  }}
                >
                  <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  {formError}
                </div>
              )}
            </div>

            <footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                padding: "16px 24px",
                borderTop: "1px solid var(--border)",
              }}
            >
              <button className="btn" onClick={() => setModalOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Create code"}
              </button>
            </footer>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        busy={deletingBusy}
        destructive
        title="Delete this code?"
        body={
          <>
            Deletes <strong>{deleting?.code}</strong>. Anyone who already
            redeemed it keeps their discount — past orders are unaffected. If you
            only want to stop new redemptions, switch it to inactive instead.
          </>
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function Toggle({
  on,
  onClick,
  onLabel,
  offLabel,
}: {
  on: boolean;
  onClick: () => void;
  onLabel: string;
  offLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className="btn"
      style={{
        gap: 9,
        background: on ? "var(--success-light)" : "#f2f4f8",
        borderColor: on ? "transparent" : "var(--border)",
        color: on ? "var(--success)" : "var(--text-muted)",
      }}
    >
      <span
        style={{
          width: 32,
          height: 18,
          borderRadius: 999,
          background: on ? "var(--success)" : "var(--border-strong)",
          position: "relative",
          flexShrink: 0,
          transition: "background 0.18s",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: on ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.18s",
          }}
        />
      </span>
      {on ? onLabel : offLabel}
    </button>
  );
}
